<template>
  <v-container class="mt-6">
    <div v-if="loading && (!book || !book.publishing_date)" class="w-100-percentage text-center">
      <v-icon class="fa-spin my-6"> fal fa-spinner </v-icon>
    </div>
    <v-row v-else class="justify-center">
      <v-col cols="12" md="6" lg="5" xl="4">
        <h2 class="text-title">
          {{ book.title[0].text }}
        </h2>
        <prismic-rich-text
          :field="book.description"
        />
        <v-list dense color="transparent">
          <v-divider />
          <v-list-item>
            <v-list-item-content>
              Category
            </v-list-item-content>
            <v-list-item-action>
              {{ book.category[0].text }}
            </v-list-item-action>
          </v-list-item>
          <v-divider />
          <v-list-item>
            <v-list-item-content>
              Book Size
            </v-list-item-content>
            <v-list-item-action>
              {{ book.size[0].text }}
            </v-list-item-action>
          </v-list-item>
          <v-divider />
          <v-list-item>
            <v-list-item-content>
              Number of Photos
            </v-list-item-content>
            <v-list-item-action>
              {{ book.photos }}
            </v-list-item-action>
          </v-list-item>
          <v-divider />
          <v-list-item>
            <v-list-item-content>
              Publishing Date
            </v-list-item-content>
            <v-list-item-action>
              {{ book.publishing_date | printdate }}
            </v-list-item-action>
          </v-list-item>
          <v-divider />
          <v-list-item>
            <v-list-item-content>
              PDF Preview
            </v-list-item-content>
            <v-list-item-action>
              <a :href="book.link[0].text" target="_blank">Download</a>
            </v-list-item-action>
          </v-list-item>
          <v-divider />
          <v-list-item>
            <v-list-item-content>
              <p class="text-h5 font-weight-bold">
                ${{ book.price[0].text }}
              </p>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn :loading="processing" dark depressed @click="buy">Buy now</v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <div v-if="book.alert && book.alert.length">
          <v-alert dark color="info" dense class="mt-6">
            <v-icon small class="mr-3">far fa-info-circle</v-icon>
            <span class="text-body-2">{{ book.alert[0].text }}</span>
          </v-alert>
        </div>
      </v-col>
      <v-col cols="12" md="6" lg="5" xl="4">
        <v-img :src="book.image.url"></v-img>
      </v-col>
    </v-row>
    <v-dialog
      v-model="personalDetailsDialog"
      width="400"
      @click:outside="
        personalDetailsDialog = false
        processing = false
      "
    >
      <v-card outlined>
        <v-card-title class="justify-center">
          <span class="text-h6 mb-2 font-weight-bold">
            Save time now
          </span>
        </v-card-title>
        <v-card-subtitle class="text-center">
          <span class="text-body-1">
            You don't need an account to check out
          </span>
        </v-card-subtitle>
        <v-card-text>
          <v-form
            ref="form"
            v-model="personalDetailsFormValid"
            lazy-validation
            @submit.prevent="noop"
          >
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="guest.email"
                  :rules="guestEmailRules"
                  label="Your email"
                  validate-on-blur
                  outlined
                  required
                ></v-text-field>
                <v-text-field
                  v-model="guest.confirmEmail"
                  :rules="guestConfirmEmailRules"
                  label="Confirm your email"
                  validate-on-blur
                  outlined
                  required
                ></v-text-field>
                <v-btn
                  :dark="personalDetailsFormValid"
                  :light="!personalDetailsFormValid"
                  :disabled="!personalDetailsFormValid"
                  :loading="checkEmail"
                  type="submit"
                  depressed
                  block
                  large
                  class="ma-0"
                  @click="buy"
                >
                  Continue
                </v-btn>
                <div class="mt-6">
                  <span class="text-caption">
                    Your email address will be used only to send you the order
                    confirmation.
                  </span>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="accountAlreadyExistDialog"
      width="400"
      @click:outside="accountAlreadyExistDialog = false"
    >
      <v-card outlined>
        <v-card-title>
          <span class="text-body-2">
            An account with your email address:
            <strong>{{ guest.email }}</strong> already exist, please login
            before proceed with your order
          </span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-btn
                dark
                depressed
                block
                large
                class="ma-0"
                @click="setCookieAndLogin"
              >
                Login
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'

export default {
  filters: {
    printdate: date => {
      return moment(date).format('Do MMMM YYYY')
    }
  },
  data() {
    return {
      loading: true,
      book: undefined,
      stripe: undefined,
      stripeCustomer: undefined,
      processing: false,
      checkEmail: false,
      accountAlreadyExistDialog: false,
      personalDetailsDialog: false,
      personalDetailsFormValid: false,
      salesTermsDialog: false,
      guest: {
        email: ''
      },
      guestEmailRules: [
        v => !!v || 'Please enter your email address',
        v =>
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
            v
          ) || 'Please enter a valid email address'
      ],
      guestConfirmEmailRules: [
        v => !!v || 'Please confirm your email',
        v => v === this.guest.email || 'Your email should match'
      ]
    }
  },
  computed: {
    ...mapState({
      me: state => state.auth.user
    })
  },
  async mounted() {
    this.stripe = this.$stripe.import()
    const uid = this.$route.params.uid
    
    try {
      const { data } = await this.$prismic.api.getByUID('book', uid)
      this.book = data
    } catch (error) {
      this.$asa.appInsights('Pages: books/_uid.vue - method: mounted', error)
    } finally {
      this.loading = false
    }

    if (this.me && this.me.stripeId) {
      const stripeId = this.me.stripeId
      try {
        this.processing = true
        this.stripeCustomer = await this.$axios.$post(
          `${process.env.baseUrl}/proxy/stripe-get-customer`,
          { stripeId }
        )
      } catch (error) {
        this.$asa.appInsights('Pages: books/_uid.vue - method: mounted', error)
      } finally {
        this.processing = false
      }
    }
  },
  methods: {
    setCookieAndLogin() {
      this.$cookies.set('100asa-redirect', `/books/aeternum-vol-1`, {
        path: '/',
        maxAge: 60 * 60 * 1, // one hour
        secure: true,
        sameSite: 'none'
      })
      this.$router.push({
        path: '/login'
      })
    },
    async buy() {
      this.processing = true

      // Set Guest email if user is not logged in
      if (!this.me && !this.guest.email) {
        this.personalDetailsDialog = true
        return
      }

      const productId = process.env.envSlot === 'production' ? this.book.stripe_product_id[0].text : 'prod_MbGBrIvSKTl7ai'
      let customer

      try {
        // Check if Guest email exist before continuing
        if (!this.me && this.guest.email) {
          this.checkEmail = true
          const userExist = await this.$axios.$get(
            `${process.env.apiBaseUrl}/users/validate-user-by-email/${this.guest.email}`
          )
          this.checkEmail = false
          this.personalDetailsDialog = false
          if (userExist) {
            this.accountAlreadyExistDialog = true
            return
          }
        }

        // Create a Customer if doesn't exist on Stripe
        if (this.stripeCustomer) {
          customer = this.stripeCustomer
        } else {
          const payload = this.me
            ? { user: this.me }
            : { user: { email: this.guest.email, name: '' } }
          customer = await this.$axios.$post(
            `${process.env.baseUrl}/proxy/stripe-create-customer`,
            payload
          )
          if (this.me) {
            const user = this.me
            user.stripeId = customer.id
            await this.$store.dispatch('account/patchUserProfile', user)
            await this.$auth.fetchUser()
          }
        }

        // Get all prices for the Product
        const prices = await this.$axios.$get(
          `${process.env.baseUrl}/proxy/stripe-list-product-prices?product=${productId}`
        )

        const priceId = prices.data[0].id

        // Create a payment session
        const sessionData = {
          priceId,
          customer: customer.id,
          book: this.book.title[0].text,
          sku: this.book.sku[0].text
        }
        const { sessionId } = await this.$axios.$post(
          `${process.env.baseUrl}/proxy/stripe-create-book-checkout-session`,
          sessionData
        )
        await this.stripe.redirectToCheckout({
          sessionId
        })
      } catch (error) {
        this.$asa.appInsights('Pages: books/_uid.vue - method: buy', error)
      } finally {
        this.processing = false
        this.checkEmail = false
        this.personalDetailsDialog = false
      }
    }
  }
}
</script>
